import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Spin,
  Col,
  Modal,
  Row,
  DatePicker,
  Input,
  Table as AntTable,
  Pagination,
  Grid,
} from 'antd';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { DATETIME_FRONTEND_FORMAT, DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import {
  deleteObligacionFiscalContribuyente,
  getObligacionesFiscalesContribuyente,
  patchObligacionFiscalContribuyente,
  postObligacionFiscalContribuyente,
} from 'api/empresas/obligacionesFiscalesContribuyente';
import {
  getEstatusDeObigacionesFiscales,
  getGirososDeAlcoholes,
  getObligacionesFiscales,
  getTiposDeAlcoholes,
  getUnidadesDeRecaudacion,
} from 'api/empresas';
import { useSelector } from 'react-redux';
import {
  getContenTypePorTipoDePadron,
  gettersListMap,
  getTipoDePadronPorContenType,
  postPadronesPrivateMap,
  retrievePadronesPrivateMap,
} from 'api/padrones';
import _logger from 'utils/logger';
import { toMoment } from 'utils/normalizers';
import { selectPadrones } from 'store/catalogos';
import {
  CloseCircleOutlined,
  DeleteOutlined, EyeOutlined, PlusOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { getEmpresas } from 'api/empresas/empresas';
import getAllPages from 'utils/getAllPages';
import { toInteger } from 'utils/formatters';
import getParamsPadron from '../paramsPadronesAfectados';
import { padronesEnum } from '../enums';

const ObligacionesFiscalesContribuyente = ({
  contribuyente,
  tipoDePadron,
  padron,
  callback,
  fetchCallback,
  mutable,
  allowAdd,
  allowCancel,
  logs,
}) => {
  const screens = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const estatus_de_obligaciones_fiscal = Form.useWatch('estatus_de_obligaciones_fiscal', form);
  const tipo_de_padron = Form.useWatch('tipo_de_padron', form);
  const object_id_padron = Form.useWatch('object_id_padron', form);
  const padrones = useSelector(selectPadrones)
    .filter((e) => ![1, 2, 3, 4, 5, 9, 13, 15, 16, 17, 19, 29, 30].includes(e.id));
  const [data, setData] = useState({ page: 1, results: [] });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [obligaciones, setObligaciones] = useState([]);
  const [estatusDeObligaciones, setEstatusObligaciones] = useState([]);
  const [subPadrones, setSubPadrones] = useState({});
  const [empresas, setEmpresas] = useState([]);
  const [unidadesRecaudadoras, setUnidadesRecaudadoras] = useState([]);
  const [tiposDeAlcoholes, setTiposDeAlcoholes] = useState([]);
  const [girosDeAlcoholes, setGirosDeAlcoholes] = useState([]);
  const isCanceling = visible === 'cancel';

  const descripcionPadron = padrones.find((e) => e.id === tipo_de_padron)?.descripcion || '';
  const optionsPadron = (subPadrones[tipo_de_padron] || []).concat([{ label: `¿No encuentra su ${descripcionPadron}? Seleccione esta opción para agregar`, id: 0 }]);
  const padronFound = padron || optionsPadron.find((e) => e.id === object_id_padron);

  const fechaFinRequired = [2, 3].includes(estatus_de_obligaciones_fiscal);
  const selected = data.results.find((e) => e.id === selectedRowKeys[0]);

  const fetchData = async (page = 1) => {
    setLoading(true);
    const params = { contribuyente: contribuyente.id, page };
    if (padron) {
      params.clave_cuenta_de_padron_obligado = padron.clave;
    }
    const _data = await getObligacionesFiscalesContribuyente(params);
    const _padrones = await Promise.all(_data.results.map((ob) => retrievePadronesPrivateMap
      ?.[getTipoDePadronPorContenType(ob.content_type_padron)](ob.object_id_padron)));
    _padrones.forEach((p, idx) => {
      _data.results[idx].establecimiento = p?.empresa?.nombre_comercial;
    });
    _data.page = page;
    setData(_data);
    fetchCallback(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      const promises = [
        getObligacionesFiscales(),
        getEstatusDeObigacionesFiscales(),
      ];
      if (!padron) {
        promises.push(getUnidadesDeRecaudacion());
        promises.push(getEmpresas({ contribuyente: contribuyente.id }, true));
        promises.push(getTiposDeAlcoholes());
        promises.push(getGirososDeAlcoholes());
      }
      const [
        _obligaciones,
        _estatus,
        _unidadesRecaudacion,
        _empresas,
        _tiposDeAlcoholes,
        _girosDeAlcoholes,
      ] = await Promise.all(promises);
      await fetchData();
      if (mounted) {
        const filtered = padron
          ? _obligaciones.filter((o) => o.tipo_padron === tipoDePadron)
          : _obligaciones;
        setObligaciones(filtered);
        setEstatusObligaciones(_estatus);
        setEmpresas(_empresas);
        setUnidadesRecaudadoras(_unidadesRecaudacion);
        setTiposDeAlcoholes(_tiposDeAlcoholes);
        setGirosDeAlcoholes(_girosDeAlcoholes);
        if (padron) setSubPadrones({ ...subPadrones, [tipoDePadron]: [padron] });
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  const fillValuesPadron = (_padron = padronFound) => {
    if (_padron) {
      form.setFieldsValue({
        object_id_padron: _padron.id,
        clave_cuenta_de_padron_obligado: _padron?.clave,
        contribuyente_rfc: _padron?.empresa?.contribuyente?.rfc,
      });
    }
  };

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setSelectedRowKeys([]);
  };

  const getDetail = (values, key, _padron) => {
    const descripcionObligacion = obligaciones
      .find((o) => o.id === values.obligacion_fiscal)?.descripcion;
    const movementType = key ? 'editada' : 'registrada';
    const __padron = _padron || padronFound;
    const padronToStr = `${_padron?.id} - ${__padron.clave} - ${__padron.empresa?.nombre_comercial
          || __padron.empresa?.contribuyente?.nombre_completo}`;
    const detailItems = [
      `Fecha inicio: ${values.fecha_inicio.format()}`,
      values.fecha_fin ? `Fecha fin: ${values.fecha_fin.format() || 'N/A'}` : null,
      `Estatus: ${estatusDeObligaciones.find((e) => e.id === values.estatus_de_obligaciones_fiscal)?.descripcion}`,
      values.observaciones ? `Observaciones: ${values.observaciones}` : null,
    ].filter((e) => e);
    const detail = detailItems.join(', ');
    return `Obligación ${descripcionObligacion} ${padron ? '' : `para ${descripcionPadron} ${padronToStr}`} [${movementType}]: ${detail}`;
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const {
        empresa,
        unidad_recaudadora,
        object_id_padron: _object_id_padron,
        registro_patronal_imss,
        actividad_empresarial_especifica,
        es_arrendamiento,
        tipo_de_alcohol,
        giro_de_alcohol,
        numero_de_permiso,
        ...values
      } = form.getFieldsValue();
      const [key] = selectedRowKeys;
      values.contribuyente = contribuyente.id;
      values.content_type_padron = getContenTypePorTipoDePadron(values.tipo_de_padron);
      if (padron && !key) {
        values.object_id_padron = padron.id;
      }
      let _padron;
      if (object_id_padron === 0) {
        _padron = await postPadronesPrivateMap[tipo_de_padron]({
          descripcion: `IMPUESTO ${descripcionPadron.toUpperCase()}`,
          empresa,
          unidad_recaudadora,
          registro_patronal_imss,
          actividad_empresarial_especifica,
          es_arrendamiento,
          tipo_de_alcohol,
          giro_de_alcohol,
          numero_de_permiso,
        });
        if (!_padron) {
          setLoading(false);
          return;
        }
        values.object_id_padron = _padron.id;
        values.clave_cuenta_de_padron_obligado = _padron.clave;
      } else {
        values.object_id_padron = object_id_padron;
      }
      if (isCanceling) {
        values.estados_globales = 5;
      }
      const saved = await (key
        ? patchObligacionFiscalContribuyente(key, values)
        : postObligacionFiscalContribuyente(values));
      if (saved) {
        saved.message = getDetail(values, key, _padron || { id: object_id_padron });
        onCancel();
        await fetchData();
        callback(saved);
      } else if (object_id_padron === 0) {
        fillValuesPadron(_padron);
        setSubPadrones({
          ...subPadrones,
          [tipo_de_padron]: [...subPadrones[tipo_de_padron], _padron],
        });
        form.setFieldsValue({ object_id_padron: _padron.id });
      }
      setLoading(false);
    } catch (err) {
      _logger(err, setLoading);
    }
  };

  const retrievePadron = async (id, tipoPadron, prop) => {
    const _padron = await retrievePadronesPrivateMap[tipoPadron](id);
    if (_padron) {
      setSubPadrones({ ...subPadrones, [tipoPadron]: [_padron] });
      form.setFieldsValue({ [prop]: _padron.id });
      if (prop === 'object_id_padron') fillValuesPadron(_padron);
    }
  };

  const onClickView = async () => {
    setLoading(true);
    if (padron) {
      fillValuesPadron();
    } else {
      await retrievePadron(
        selected.object_id_padron,
        getTipoDePadronPorContenType(selected.content_type_padron),
        'object_id_padron',
      );
      if (selected.object_id_padron_padre_dependiente) {
        await retrievePadron(
          selected.object_id_padron_padre_dependiente,
          getTipoDePadronPorContenType(selected.content_type_padron),
          'object_id_padron_padre_dependiente',
        );
      }
    }
    setLoading(false);
    setVisible(true);
  };

  const onClickCancel = async () => {
    await onClickView();
    setVisible('cancel');
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
    form.setFieldsValue({ tipo_padron: tipoDePadron });
  };

  const onClickDelete = () => setVisibleAlert(true);

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteObligacionFiscalContribuyente(key);
    if (deleted) {
      callback({ message: getDetail(selected, key, { id: object_id_padron }) });
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      title: 'Clave de Registro Estatal',
      key: 'clave_cuenta_de_padron_obligado',
      dataIndex: 'clave_cuenta_de_padron_obligado',
      width: 150,
    },
    {
      title: 'Nombre del Establecimiento',
      key: 'establecimiento',
      dataIndex: 'establecimiento',
      width: 250,
    },
    {
      title: 'Obligación Fiscal',
      key: 'obligacion_fiscal',
      dataIndex: 'obligacion_fiscal',
      width: 250,
      render: (val) => obligaciones.find((e) => e.id === val)?.descripcion,
    },
    {
      title: 'Fecha Inicio',
      key: 'fecha_inicio',
      dataIndex: 'fecha_inicio',
      width: 120,
      render: (val) => val?.format(),
      responsive: ['md'],
    },
    {
      title: 'Estado',
      key: 'estatus_de_obligaciones_fiscal',
      dataIndex: 'estatus_de_obligaciones_fiscal',
      width: 120,
      render: (val) => estatusDeObligaciones.find((e) => e.id === val)?.descripcion,
      responsive: ['md'],
    },
  ].filter((e) => e);

  const rules = {
    required: [{ required: true }],
    estatus: [
      { required: true },
      {
        validator: async () => {
          if (isCanceling && !fechaFinRequired) {
            throw new Error('Cambie estatus al que corresponda');
          }
        },
      },
    ],
  };

  const onChangeObligacion = (value) => {
    if (value && !padron) {
      const match = obligaciones.find((e) => e.id === value);
      form.setFieldsValue({ tipo_de_padron: match.tipo_padron });
    }
  };

  const onRow = (record) => ({
    onClick: () => {
      setSelectedRowKeys([record.id]);
      form.setFieldsValue({
        ...record,
        tipo_de_padron: getTipoDePadronPorContenType(record.content_type_padron),
      });
    },
    onDoubleClick: () => onClickView(),
  });

  const onChangeTipoPadron = async (_tipo_de_padron) => {
    form.resetFields(['obligacion_fiscal']);
    if (!_tipo_de_padron) return;
    setLoading(true);
    const params = getParamsPadron(
      { tipo_de_padron: _tipo_de_padron },
      padronesEnum.CONTRIBUYENTE,
      { id: contribuyente.id },
    );
    const _padrones = await getAllPages(gettersListMap[_tipo_de_padron], params);
    setSubPadrones({ ...subPadrones, [_tipo_de_padron]: _padrones });
    setLoading(false);
  };

  const onSelect = (s) => fillValuesPadron(optionsPadron.find((e) => e.id === s));

  const estatusDeObligacionesMapped = estatusDeObligaciones.map((e) => ({
    ...e,
    disabled: ((!selectedRowKeys.length && [2, 3].includes(e.id))
    || (isCanceling && [1, 4].includes(e.id))),
  }));

  const getOnFinishText = () => {
    if (isCanceling) return 'Cancelar';
    return undefined;
  };

  const getOnFinishIcon = () => {
    if (isCanceling) return <CloseCircleOutlined />;
    return undefined;
  };

  const getLabel = () => {
    if (isCanceling) return 'Cancelar';
    return selectedRowKeys.length ? 'Ver' : 'Agregar';
  };

  const label = getLabel();

  const regexp = new RegExp(`${descripcionPadron}[\\s]+${object_id_padron}`, 'i');
  const canDelete = logs.some((l) => l.comentario?.id && regexp.test(l.comentario.comentario));

  return (
    <>
      <Spin tip="Cargando..." spinning={loading} style={{ width: '100%' }}>
        <FormSubmitCustomControls
          controls={[
            mutable && allowAdd && {
              text: 'Agregar',
              icon: <PlusOutlined />,
              onClick: onClickAdd,
            },
            {
              text: 'Ver',
              icon: <EyeOutlined />,
              onClick: () => onClickView(),
              disabled: !selectedRowKeys.length,
            },
            mutable && allowAdd && {
              text: 'Eliminar',
              icon: <DeleteOutlined />,
              onClick: onClickDelete,
              disabled: !canDelete,
            },
            mutable && allowCancel && {
              text: 'Cancelar',
              icon: <CloseCircleOutlined />,
              onClick: onClickCancel,
              disabled: !selectedRowKeys.length || selected?.estados_globales !== 1 || canDelete,
            },
          ].filter((e) => e)}
          style={{ width: '100%' }}
          justify="end"
        />
        <Table
          columns={columns}
          dataSource={data?.results}
          rowSelection={rowSelection}
          pagination={false}
          onRow={onRow}
          rowKey="id"
          scroll={{
            y: 600,
            x: columns.reduce((acum, curr) => acum + curr.width, 0),
          }}
          size={(screens.md) ? 'small' : 'large'}
        />
        <Pagination
          current={data.page}
          onChange={(p) => fetchData(p)}
          total={data.count}
        />
      </Spin>
      <Modal
        visible={!!visible}
        closable={false}
        footer={null}
        title={(
          <FormSubmitCustomControls
            label={`${label} Obligación Fiscal`}
            onFinish={mutable && label !== 'Ver' ? form.submit : null}
            onCancel={onCancel}
            loading={loading}
            onFinishText={getOnFinishText()}
            onFinishIcon={getOnFinishIcon()}
          />
        )}
        keyboard={!loading}
        maskClosable={!loading}
        onCancel={onCancel}
        forceRender
      >
        <Spin tip="Cargando..." spinning={loading}>
          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              onFinish={() => onFinish()}
              initialValues={{
                estatus_de_obligaciones_fiscal: 1,
                fecha_inicio: toMoment(new Date()),
                unidad_recaudadora: contribuyente.unidad_de_recaudacion,
              }}
            >
              <Row gutter={10}>
                <Col span={padron ? 0 : 24}>
                  <Form.Item
                    name="tipo_de_padron"
                    label="Padrón"
                    rules={!padron ? rules.required : []}
                  >
                    <Select
                      options={padrones}
                      onChange={onChangeTipoPadron}
                      allowClear
                      disabled={!!selectedRowKeys.length}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="obligacion_fiscal"
                    label="Obligación Fiscal"
                    rules={rules.required}
                  >
                    <Select
                      options={obligaciones.filter((o) => o.tipo_padron === tipo_de_padron)}
                      onChange={onChangeObligacion}
                      disabled={!mutable || !!selectedRowKeys.length}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col span={padron || !descripcionPadron ? 0 : 24}>
                  <Form.Item
                    name="object_id_padron"
                    label={descripcionPadron}
                    rules={!padron ? rules.required : []}
                    tooltip={`Seleccione su ${descripcionPadron}, si no la ve en la lista seleccione la opción para agregar y capture la información necesaria`}
                  >
                    <Select
                      options={optionsPadron}
                      allowClear
                      getLabel={(_padron) => _padron.label
                        || `${_padron?.clave} - ${_padron?.empresa?.nombre_comercial || _padron?.empresa?.contribuyente?.nombre_completo}`}
                      disabled={!!selectedRowKeys.length}
                      onSelect={onSelect}
                    />
                  </Form.Item>
                </Col>
                {object_id_padron === 0 && (
                  <>
                    <Col span={24}>
                      <Form.Item
                        name="empresa"
                        label="Establecimiento"
                        rules={rules.required}
                      >
                        <Select
                          options={empresas}
                          getLabel={(em) => `${em?.clave} - ${em?.nombre_comercial || em?.contribuyente?.nombre_completo}`}
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name="unidad_recaudadora"
                        label="Unidad Recaudadora"
                        rules={rules.required}
                        tooltip="Seleccione la Unidad de Recaudación más cercana a su domicilio"
                      >
                        <Select
                          options={unidadesRecaudadoras}
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                    {tipo_de_padron === padronesEnum.NOMINA && (
                      <Col span={24}>
                        <Form.Item
                          name="registro_patronal_imss"
                          label="Número de registro IMSS"
                          rules={rules.required}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                    )}
                    {tipo_de_padron === padronesEnum.CEDULAR && (
                      <Col span={24}>
                        <Form.Item
                          name="actividad_empresarial_especifica"
                          label="Actividad Empresarial Específica"
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                    )}
                    {tipo_de_padron === padronesEnum.CEDULAR && (
                      <Col span={24}>
                        <Form.Item
                          name="es_arrendamiento"
                          label="¿Es Arrendamiento?"
                        >
                          <Select trueFalse />
                        </Form.Item>
                      </Col>
                    )}
                    {tipo_de_padron === padronesEnum.ALCOHOL && (
                      <>
                        <Col span={24}>
                          <Form.Item
                            name="tipo_de_alcohol"
                            label="Tipo de alcohol"
                            rules={rules.required}
                          >
                            <Select options={tiposDeAlcoholes} />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            name="giro_de_alcohol"
                            label="Giro de alcohol"
                            rules={rules.required}
                          >
                            <Select options={girosDeAlcoholes} />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            name="numero_de_permiso"
                            label="Número de permiso"
                            rules={rules.required}
                            normalize={toInteger}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                  </>
                )}
                {!!object_id_padron && (
                  <>
                    <Col span={12}>
                      <Form.Item
                        name="clave_cuenta_de_padron_obligado"
                        label="Clave"
                      >
                        <Input allowClear disabled />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="contribuyente_rfc"
                        label="RFC"
                      >
                        <Input allowClear disabled />
                      </Form.Item>
                    </Col>
                  </>
                )}
                <Col span={24}>
                  <Form.Item
                    label="Fecha Inicio"
                    name="fecha_inicio"
                    rules={rules.required}
                  >
                    <DatePicker
                      placeholder=""
                      format={DATE_FRONTEND_FORMAT}
                      disabled={!mutable || !!selectedRowKeys.length}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="fecha_fin"
                    label="Fecha Fin"
                    rules={fechaFinRequired ? rules.required : []}
                  >
                    <DatePicker
                      placeholder=""
                      format={DATE_FRONTEND_FORMAT}
                      showTime
                      disabled={!mutable || visible !== 'cancel'}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="estatus_de_obligaciones_fiscal"
                    label="Estado"
                    rules={rules.estatus}
                  >
                    <Select
                      options={estatusDeObligacionesMapped}
                      onChange={(estatus) => {
                        if (estatus === 4) {
                          form.setFieldsValue({
                            fecha_de_inicio_de_dependiente: toMoment(new Date()),
                          });
                        }
                      }}
                      allowClear
                      disabled={!mutable || label === 'Ver'}
                    />
                  </Form.Item>
                </Col>
                {estatus_de_obligaciones_fiscal === 4 && (
                  <>
                    <Col span={24}>
                      <Form.Item
                        name="object_id_padron_padre_dependiente"
                        label={descripcionPadron}
                        rules={rules.required}
                      >
                        <Select
                          allowClear
                          options={optionsPadron.filter((e) => e.id && e.id !== object_id_padron)}
                          getLabel={(_padron) => `${_padron?.clave} - ${_padron?.empresa?.nombre_comercial || _padron?.empresa?.contribuyente?.nombre_completo}`}
                          disabled={!!selectedRowKeys.length}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name="fecha_de_inicio_de_dependiente"
                        label="Fecha de inicio de dependiente"
                      >
                        <DatePicker
                          placeholder=""
                          format={DATETIME_FRONTEND_FORMAT}
                          showTime
                          rules={rules.required}
                          disabled={!!selectedRowKeys.length}
                        />
                      </Form.Item>
                    </Col>
                  </>
                )}
                <Col span={24}>
                  <Form.Item
                    name="observaciones"
                    label="Observaciones"
                    rules={[2, 3].includes(estatus_de_obligaciones_fiscal) ? rules.required : []}
                  >
                    <Input.TextArea
                      autoSize={{ minRows: 2, maxRows: 3 }}
                      disabled={!mutable || visible !== 'cancel'}
                    />
                  </Form.Item>
                </Col>
                {!!selectedRowKeys.length && !!padron && (
                  <Col span={24}>
                    <Form.Item
                      name="fecha_ultimo_estado"
                      label="Fecha Último Estado"
                    >
                      <DatePicker
                        placeholder=""
                        format={DATETIME_FRONTEND_FORMAT}
                        showTime
                        disabled
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Col>
        </Spin>
      </Modal>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => {
          setVisibleAlert(false);
          setSelectedRowKeys([]);
        }}
        visible={visibleAlert}
        content=" el registro seleccionado?"
        loading={loading}
      />
    </>
  );
};

const Table = styled(AntTable)`
  .ant-table-thead>tr>th{
    background: transparent;
    border-bottom: 2px solid;
  }
`;

ObligacionesFiscalesContribuyente.propTypes = {
  contribuyente: PropTypes.shape().isRequired,
  tipoDePadron: PropTypes.number,
  callback: PropTypes.func.isRequired,
  fetchCallback: PropTypes.func.isRequired,
  padron: PropTypes.shape({ id: PropTypes.number, clave: PropTypes.string }),
  mutable: PropTypes.bool,
  allowAdd: PropTypes.bool,
  allowCancel: PropTypes.bool,
  logs: PropTypes.arrayOf(PropTypes.shape()),
};

ObligacionesFiscalesContribuyente.defaultProps = {
  tipoDePadron: null,
  padron: null,
  mutable: true,
  allowAdd: false,
  allowCancel: false,
  logs: [],
};

export default ObligacionesFiscalesContribuyente;
